/**
 * Created by Ning Jiang on 2021-05-04.
 */

import {SerialParsedData} from "./SerialParsedData";
import {IInfectiousWays, IJumpingS2Data, ISerialData, ISerialGameData, IxWaysData} from "./ISerialData";
import {SerialDataParser} from "./SerialDataParser";
import {IWinRatioConfig} from "../../../core/config/WinRatio";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {SerialGameConfig} from "../../SerialGameConfig";
import {StackedSymbol} from "../../../core/reel/stackedsymbol/StackedSymbol";
import {SerialGameMode} from "../SerialGameMode";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {Serial} from "../../Serial";
import {NolimitConfig} from "@nolimitcity/slot-launcher/bin/settings/NolimitConfig";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";

export interface IParsedBetWayWinData {
    symbol:string;
    amount:number;
    multiplier:number;
    multipliedAmount:number;
    length:number;
    reelWinPositions:boolean[][];
    numberOfCombinations:number;
}

export class SerialParsedGameData extends SerialParsedData {

    public readonly playedBetValue:number;
    public readonly wasFeatureBuy:boolean;
    public readonly brokeTheBank:boolean;
    public readonly mysterySymbolReveal:string;
    public readonly scatterToxWaysTriggered:boolean;
    public readonly xWaysDataList:IxWaysData[];
    public readonly totalSpinWinnings:number;
    public readonly maximumWinAchieved:boolean;
    public readonly jumpingS2:IJumpingS2Data;
    public readonly infectiousWays:IInfectiousWays[];
    public readonly evaluatedAreaBeforeFeature:string[][];

    // Calculated values
    public betWins:IParsedBetWayWinData[];
    public betWinsBeforeReveal:IParsedBetWayWinData[];
    public winPositions:boolean[][];
    public readonly currentWinRatioConfig:IWinRatioConfig | null;
    public readonly nearWinReels:boolean[];
    public readonly nearWinBigWinReels:boolean[];

    // Nudge Data
    public readonly newXNudge:boolean[][];
    public readonly nudgeSteps:number[];

    constructor(rawData:ISerialGameData, parser:SerialDataParser) {
        super(rawData, parser);

        if(NolimitConfig.isDevMode) {
            this.verifyReels();
        }

        this.evaluatedAreaBeforeFeature = rawData.evaluatedAreaBeforeFeature;
        this.playedBetValue = rawData.playedBetValue;
        this.wasFeatureBuy = rawData.wasFeatureBuy;
        this.brokeTheBank = rawData.brokeTheBank;
        this.mysterySymbolReveal = rawData.mysterySymbolReveal;
        this.scatterToxWaysTriggered = this.parseScatterToxWaysTriggered();
        this.xWaysDataList = rawData.xWaysDataList;
        this.maximumWinAchieved = rawData.maximumWinAchieved;
        this.jumpingS2 = parser.parseJumpingS2(rawData);
        this.infectiousWays = rawData.infectiousWays.concat();

        this.newXNudge = this.parseNewXNudge();
        this.nudgeSteps = this.parseNudgeSteps();

        this.parseWins(rawData);
        this.totalSpinWinnings = this.mode === SerialGameMode.CAPWIN_GAME ?
            this.betWins.reduce((a, b) => {
                return a + b.multipliedAmount;
            }, 0) : rawData.totalSpinWinnings;
        this.currentWinRatioConfig = parser.parseWinRatio(this.totalSpinWinnings / this.playedBetValue);

        this.nearWinReels = this.parseNearWinReels(this.scatterPositions, this.wasFeatureBuy);
        this.nearWinBigWinReels = this.getRandomBigWinNearWin();
    }

    private verifyReels():void {
        this.reels.forEach((reel, reelId) => {
            const middleReel = reel.slice(1, 4);
            if(middleReel.filter(sym => sym.includes("S")).length > 1) {
                debugger;
                throw new Error(`Server bug: more than 1 scatter on the same reel!`);
            }

            if(middleReel.some(sym => sym.includes("S")) && this.reelHasWXN(reelId)) {
                debugger;
                throw new Error(`Server bug: scatter and WXN cannot be on the same reel!`);
            }

            if(middleReel.some(sym => sym === "X") && this.reelHasWXN(reelId)) {
                debugger;
                throw new Error(`Server bug: xWays and WXN cannot be on the same reel!`);
            }

            // if(this.mode.includes("RESPIN")) {
            //     Serial.model.currentSpinPositions.forEach((reelSpinPositions, reelId) => {
            //         if(!this.reelHasNewWXN(reelId)) {
            //             return;
            //         }
            //
            //         if(reelSpinPositions.some((spinPosition, symId) => {
            //             return !spinPosition && !this.fullReels[reelId][symId].includes("WXN")
            //         })) {
            //             debugger;
            //             throw new Error(`Server bug: WXN cannot appear on a respin reel with sticky symbol other than WXN!`);
            //         }
            //     });
            // }
        });
    }

    public parseScatterPositions(rawData:ISerialData):number[] {
        const result = ArrayHelper.initArrayWithValues(5, reelId => -1);
        if(rawData.mode === SerialGameMode.CAPWIN_GAME) {
            return result;
        }

        if(rawData.specialSymbolsList) {
            rawData.specialSymbolsList.forEach(symbol => {
                result[symbol.reel] = symbol.currentRow;
            });
        }

        return result;
    }

    private parseNearWinReels(scatterPositions:number[], wasFeatureBuy:boolean):boolean[] {
        const result = ArrayHelper.initArrayWithValues(SerialGameConfig.REELS_NUM, reelId => false);
        if(this.mode === SerialGameMode.CAPWIN_GAME) {
            return result;
        }
        if(wasFeatureBuy) {
            return result;
        }

        // reel1
        if(scatterPositions[2] > -1 && !Serial.model.currentSpinPositions[2][scatterPositions[2]] &&
            scatterPositions[3] > -1 && !Serial.model.currentSpinPositions[3][scatterPositions[3]]) {
            if(scatterPositions[1] === -1 || Serial.model.currentSpinPositions[1][scatterPositions[1]]) {
                result[1] = true;
            }
        }

        // reel2
        if(scatterPositions[1] > -1 &&
            scatterPositions[3] > -1 && !Serial.model.currentSpinPositions[3][scatterPositions[3]]) {
            if(scatterPositions[2] === -1 || Serial.model.currentSpinPositions[2][scatterPositions[2]]) {
                result[2] = true;
            }
        }

        if(scatterPositions[1] > -1 && scatterPositions[2] > -1) {
            if(scatterPositions[3] === -1 || Serial.model.currentSpinPositions[3][scatterPositions[3]]) {
                result[3] = true;
            }

            if(scatterPositions[3] > -1) {
                if(scatterPositions[4] === -1 || Serial.model.currentSpinPositions[4][scatterPositions[4]]) {
                    result[4] = true;
                }
            }
        }

        return result;
    }

    private reelHasWXN(reelId:number):boolean {
        if(this.mode === SerialGameMode.CAPWIN_GAME) {
            return false;
        }
        return this.fullReels[reelId].some((sym, symId) => {
            if(symId < 2 || symId > 4) {
                return sym.includes("WXN") && Serial.enhancerReels[reelId][symId].isOpened;
            } else {
                return sym.includes("WXN");
            }
        });
    }

    private parseScatterToxWaysTriggered():boolean {
        if(this.mode === SerialGameMode.CAPWIN_GAME) {
            return false;
        }
        if(this.nextMode != GameMode.NORMAL) {
            return false;
        }

        const ids = [1, 2, 3].filter(reelId => this.scatterPositions[reelId] > -1);
        return ids.length === 2;
    }

    private parseWins(rawData:ISerialGameData):void {
        this.betWins = [];
        this.betWinsBeforeReveal = [];
        this.winPositions = ArrayHelper.initArrayWithValues<boolean[]>(SerialGameConfig.REELS_NUM, () => {
            return ArrayHelper.initArrayWithValues<boolean>(7, () => false)
        });
        rawData.betWayWins.forEach((win) => {
            const betWin = {
                symbol : win.symbol,
                amount : win.amount,
                multiplier : win.multiplier,
                multipliedAmount : win.multipliedAmount,
                length : win.length,
                numberOfCombinations : win.numberOfCombinations,
                reelWinPositions : ArrayHelper.initArrayWithValues<boolean[]>(SerialGameConfig.REELS_NUM, () => {
                    return ArrayHelper.initArrayWithValues<boolean>(7, () => false)
                })
            };
            const betWinBeforeReveal = {
                symbol : win.symbol,
                amount : win.amount,
                multiplier : win.multiplier,
                multipliedAmount : win.multipliedAmount,
                length : win.length,
                numberOfCombinations : win.numberOfCombinations,
                reelWinPositions : ArrayHelper.initArrayWithValues<boolean[]>(SerialGameConfig.REELS_NUM, () => {
                    return ArrayHelper.initArrayWithValues<boolean>(7, () => false)
                })
            };
            for(let reelId = 0; reelId < win.length; reelId++) {
                const reel = rawData.evaluatedArea[reelId];
                reel.forEach((sym, symId) => {
                    // unOpened respin enhancer
                    if((symId === 0 || symId === 6) && !Serial.enhancerReels[reelId][symId].isOpened) {
                        return;
                    }
                    // unOpened scatter enhancer
                    if((reelId > 0 && reelId < 4) && (symId === 1 || symId === 5) && !(Serial.enhancerReels[reelId][symId].isOpened || this.scatterPositions[reelId] > 0)) {
                        return;
                    }
                    if((sym.includes(win.symbol) || sym.includes("W") || sym.includes("S2")) && reelId < betWin.length) {
                        betWin.reelWinPositions[reelId][symId] = true;
                        betWinBeforeReveal.reelWinPositions[reelId][symId] = true;
                        this.winPositions[reelId][symId] = true;
                        return;
                    }
                    // S2 shows as a wild in evaluatedArea.
                });
            }

            if(this.xWaysDataList) {
                this.xWaysDataList.forEach(xWays => {
                    betWinBeforeReveal.reelWinPositions[xWays.symbolPosition[0]][xWays.symbolPosition[1]] = false;
                });
            }

            this.betWins.push(betWin);
            for(let rId = 0; rId < betWinBeforeReveal.length; rId++) {
                if(betWinBeforeReveal.reelWinPositions[rId].indexOf(true) == -1) {
                    return;
                }
            }
            this.betWinsBeforeReveal.push(betWinBeforeReveal);
        });

        // if there is X longer than the winCases, should be true in respin.
        if(this.nextMode.includes("RESPIN")) {
            for(let reelId = 0; reelId < 5; reelId++) {
                const reel = rawData.evaluatedArea[reelId];
                reel.forEach((sym, symId) => {
                    // unOpened respin enhancer
                    if((symId === 0 || symId === 6) && !Serial.enhancerReels[reelId][symId].isOpened) {
                        return;
                    }
                    // unOpened scatter enhancer
                    if((reelId > 0 && reelId < 4) && (symId === 1 || symId === 5) && !(Serial.enhancerReels[reelId][symId].isOpened || this.scatterPositions[reelId] > 0)) {
                        return;
                    }
                    if(sym.includes("X")) {
                        this.winPositions[reelId][symId] = true;
                    }
                });
            }
        }

        //  add scatter positions to allWinPositions.
        if(this.nextMode === GameMode.RESPIN || this.freespinTriggeredThisSpin) {
            this.scatterPositions.forEach((symId, reelId) => {
                if(symId > -1) {
                    this.winPositions[reelId][symId] = true;
                }
            })
        }

        // if(this.scatterPositions[4] > -1) {
        //     this.winPositions[4][this.scatterPositions[4]] = true;
        // }

        // if infectious ways symbol and the symbol is part of win
        this.infectiousWays.forEach(infectious => {
            if(this.betWins.some(win => win.symbol === infectious.symName)) {
                this.winPositions[infectious.reel][infectious.row] = true;
            }
        });
    }

    private getRandomBigWinNearWin():boolean[] {
        const values = ArrayHelper.initArrayWithValues(SerialGameConfig.REELS_NUM, () => false);
        if(this.mode === SerialGameMode.CAPWIN_GAME) {
            return values;
        }
        const winBeforeReveal = this.betWinsBeforeReveal.reduce((a, b) => {
            return a + b.multipliedAmount;
        }, 0);

        values[4] = this.getRandomBigWinTease(winBeforeReveal / this.playedBetValue);

        // let reelId = 0;
        // values[reelId] = this.getRandomBigWinTease(previousWin / this.playedBetValue);
        // while(!values[reelId] && reelId < 4) {
        //     reelId++;
        //     values[reelId] = this.getRandomBigWinTease((reelId === 4 ? this.totalSpinWinnings : previousWin) / this.playedBetValue);
        // }
        // for(reelId++; reelId <= 4; reelId++) {
        //     values[reelId] = true;
        // }

        return values;
    }

    private getRandomBigWinTease(winRatio:number):boolean {
        if(this.wasFeatureBuy) {
            return false;
        }
        if(this.mode.includes("SUPER")) {
            return winRatio >= MathHelper.randomNumberInRange(150, 200);
        }

        if(this.mode.includes("FREESPIN")) {
            return winRatio >= MathHelper.randomNumberInRange(30, 50);
        }

        return winRatio >= MathHelper.randomNumberInRange(20, 35);
    }

    public parseNewXNudge():boolean[][] {
        const result = ArrayHelper.initArrayWithValues<boolean[]>(SerialGameConfig.REELS_NUM, () => {
            return ArrayHelper.initArrayWithValues<boolean>(7, () => false)
        });

        if(this.mode === SerialGameMode.CAPWIN_GAME) {
            return result;
        }

        this.fullReels.forEach((reel, reelId) => {
            reel.forEach((sym, symId) => {
                if(symId < 2 || symId > 4) {
                    result[reelId][symId] = sym.includes("WXN") && Serial.enhancerReels[reelId][symId].isOpened && Serial.model.currentSpinPositions[reelId][symId];
                } else {
                    result[reelId][symId] = sym.includes("WXN") && Serial.model.currentSpinPositions[reelId][symId];
                }
            });
        });

        return result;
    }

    // The range is the startIndex (include) in the reel symbols to the endIndex (NOT include).
    // For a 3x5 slot, there are 3 symbols in reel, then the range is [0, 3],
    // If you have a special feature that the are actually 5 symbols but you only show 3 symbols from the bottom of the 5 symbols, then you should return [2, 5].
    private parseNudgeSteps():number[] {
        const checkRange:[number, number] = [1, 4];
        return ArrayHelper.initArrayWithValues(SerialGameConfig.REELS_NUM, reelId => {
            const nudgeId = this.newXNudge[reelId].indexOf(true);
            if(nudgeId < 0) {
                return 0;
            }
            if(nudgeId > 1 && nudgeId < 5) {
                // middle nudge
                let symbolIndex:number = -1;
                let stackIndex:number = -1;
                for(let i:number = checkRange[0]; i < checkRange[1]; i++) {
                    const stackedSymbolName = StackedSymbol.parseStackedSymbolName(this.reels[reelId][i], reelId);
                    if(stackedSymbolName.symName === "WXN") {
                        symbolIndex = i;
                        stackIndex = stackedSymbolName!.index;
                        break;
                    }
                }

                if(symbolIndex === -1) {
                    return 0;
                }

                return checkRange[0] + stackIndex - symbolIndex;
            } else {
                return this.getEnhancerNudgeStep(reelId, nudgeId);
            }
        });
    }

    private getEnhancerNudgeStep(reelId:number, symId:number):number {
        switch(symId) {
            case 0:
                return 6;
            case 1:
                return 4;
            case 5:
                return -4;
            case 6:
                return -6;
            default:
                debugger;
                throw new Error(`SerialParsedGameData.getEnhancerNudgeStep reel ${reelId}, symbol ${symId}: Server nudge symbol position error!`);
        }
    }
}